import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, FileText } from 'lucide-react';
import { useProgram } from './ProgramContext';
import { Reveal, GoldDivider, StaggerHeadline } from './components';

// Premium private-aviation hero — white airplane on airport during sunset
const HERO_IMG = 'https://images.unsplash.com/photo-1619985631958-d44cea12acfc?w=2400&q=85&auto=format&fit=crop';

const DAY_TITLES = {
  1: 'Market Reality, Structure & Positioning',
  2: 'Catering, Galley & Cabin Operations',
  3: 'Aircraft, Simulation, Interview & Final Test',
};

const DAY_SUMMARIES = {
  1: 'Break the airline myths. Install the operator posture. Map the market.',
  2: 'Catering planning, galley craft, service essentials, cabin operations and recovery.',
  3: 'Aircraft familiarisation, tabletop mission, crisis management, interview framework and the final test.',
};

// Fixed day timetables (Paris time)
const DAY_TIMETABLES = {
  1: [
    ['09:00', 'Module 1'], ['10:30', 'Break'], ['10:45', 'Module 2'], ['12:00', 'Module 3'],
    ['13:00', 'Lunch'], ['14:00', 'Module 4'], ['16:00', 'Break'], ['16:15', 'Module 5'],
    ['17:45', 'Module 6'], ['18:30', 'End'],
  ],
  2: [
    ['09:00', 'Module 7'], ['11:00', 'Break'], ['11:15', 'Module 8'], ['12:15', 'Lunch'],
    ['13:15', 'Module 9'], ['14:15', 'Module 10 (part 1)'], ['15:45', 'Break'],
    ['16:00', 'Module 10 (part 2)'], ['17:30', 'End'],
  ],
  3: [
    ['09:00', 'Module 11'], ['11:30', 'Break'], ['11:45', 'Module 12'], ['13:15', 'Lunch'],
    ['14:15', 'Module 13'], ['15:45', 'Break'],
    ['16:00', 'Module 14 (interview, group exercise, final test, closing round-table)'], ['18:30', 'End'],
  ],
};

const formatHours = (min) => {
  const h = Math.round((min / 60) * 10) / 10;
  return `${h} ${h === 1 ? 'hour' : 'hours'}`;
};
const dayMinutes = (modules, d) => modules.filter((m) => m.day === d).reduce((s, m) => s + (m.duration_min || 0), 0);

const DayTimetable = ({ day }) => {
  const rows = DAY_TIMETABLES[day];
  if (!rows) return null;
  return (
    <section style={{ padding: '0 32px 64px', maxWidth: 1280, margin: '0 auto' }} data-testid={`day-${day}-timetable`}>
      <div className="g33-eyebrow" style={{ marginBottom: 20, color: 'var(--g33-or-deep)' }}>
        Day timetable (Paris time)
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxWidth: 640 }}>
        {rows.map(([time, label], i) => (
          <li key={i} data-testid={`timetable-row-${i}`} style={{
            display: 'grid', gridTemplateColumns: '72px 1fr', gap: 16, padding: '8px 0',
            borderBottom: '1px solid var(--g33-ligne)', fontSize: 15, color: 'var(--g33-charcoal)',
          }}>
            <span style={{ color: 'var(--g33-or)', fontVariantNumeric: 'tabular-nums' }}>{time}</span>
            <span>{label}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

// =====================================================================
// PROGRAM LANDING — gate33.co-style split hero + day cards + toolkit grid
// =====================================================================
export const ProgramLanding = () => {
  const { modules, toolkit } = useProgram();

  const days = [1, 2, 3].map((d) => ({
    day: d,
    items: modules.filter((m) => m.day === d),
    title: DAY_TITLES[d],
    summary: DAY_SUMMARIES[d],
  }));

  const dayCounts = (d) => modules.filter((m) => m.day === d).length;
  const totalHours = Math.round(modules.reduce((s, m) => s + (m.duration_min || 0), 0) / 60);

  return (
    <>
      {/* HERO — gate33.co split layout */}
      <section className="g33-module-hero" style={{ paddingTop: 140 }}>
        <div className="g33-module-hero-grid">
          <Reveal y={32}>
            <div className="g33-module-hero-image">
              <motion.img
                src={HERO_IMG}
                alt="Gate 33"
                initial={{ scale: 1.06 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.8, ease: [0.2, 0.8, 0.2, 1] }}
              />
            </div>
          </Reveal>

          <div className="g33-module-hero-content">
            <Reveal delay={0.1}>
              <div className="g33-module-hero-eyebrow">
                <span className="g33-module-hero-eyebrow-rule" />
                Gate 33 · Confidential program
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <StaggerHeadline
                text="Business Aviation Cabin Attendant."
                className="g33-display g33-module-hero-title"
              />
            </Reveal>

            <Reveal delay={0.4}>
              <p className="g33-module-hero-hook">
                14 modules. 3 days. The complete transition framework — from commercial
                cabin to private aviation operator. Three live online days with Nicolas.
              </p>
            </Reveal>

            <Reveal delay={0.5}>
              <div className="g33-module-stat-grid" data-testid="program-stats">
                <div className="g33-module-stat-box">
                  <div className="g33-module-stat-value" data-testid="stat-modules">{modules.length}</div>
                  <div className="g33-module-stat-label">Modules</div>
                </div>
                <div className="g33-module-stat-box">
                  <div className="g33-module-stat-value">
                    3<span style={{ fontSize: '0.42em', marginLeft: 6, fontWeight: 400 }}>days</span>
                  </div>
                  <div className="g33-module-stat-label">Live online</div>
                </div>
                <div className="g33-module-stat-box">
                  <div className="g33-module-stat-value">
                    {totalHours}<span style={{ fontSize: '0.42em', marginLeft: 6, fontWeight: 400 }}>h</span>
                  </div>
                  <div className="g33-module-stat-label">Of modules</div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.6}>
              <Link
                to="/gate33-program/day/1"
                className="g33-btn-primary g33-module-hero-cta"
                data-testid="cta-begin-day-1"
              >
                Begin Day 1 <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THREE DAYS — three transformations */}
      <section style={{ padding: '120px 32px', maxWidth: 1280, margin: '0 auto' }}>
        <Reveal>
          <div className="g33-eyebrow" style={{ marginBottom: 16 }}>What's inside</div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2
            className="g33-display"
            style={{
              fontSize: 'clamp(40px, 5vw, 64px)',
              marginBottom: 16,
              maxWidth: 800,
            }}
          >
            Three days. Three transformations.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p
            style={{
              color: 'var(--g33-charcoal)',
              fontSize: 17,
              lineHeight: 1.7,
              maxWidth: 720,
              marginBottom: 80,
            }}
          >
            Each day is a complete arc — the market, the service, the aircraft. Open
            any day to see the full module sequence and detailed plan.
          </p>
        </Reveal>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 32,
          }}
        >
          {days.map(({ day, title, summary }, i) => (
            <Reveal key={day} delay={i * 0.1}>
              <Link
                to={`/gate33-program/day/${day}`}
                className="g33-card g33-day-card"
                data-testid={`day-card-${day}`}
              >
                <div
                  className="g33-day-numeral"
                  aria-hidden="true"
                >
                  {String(day).padStart(2, '0')}
                </div>
                <div className="g33-eyebrow" style={{ marginBottom: 24, position: 'relative' }}>
                  Day {day}
                </div>
                <h3
                  className="g33-display"
                  style={{ fontSize: 26, marginBottom: 16, lineHeight: 1.2, position: 'relative' }}
                >
                  {title}
                </h3>
                <p
                  style={{
                    color: 'var(--g33-charcoal)',
                    fontSize: 14,
                    lineHeight: 1.6,
                    marginBottom: 32,
                    position: 'relative',
                  }}
                >
                  {summary}
                </p>
                <p
                  style={{
                    color: 'var(--g33-grey-warm)',
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.18em',
                    marginBottom: 24,
                    position: 'relative',
                  }}
                >
                  <span data-testid={`day-card-${day}-meta`}>{dayCounts(day)} modules · {formatHours(dayMinutes(modules, day))}</span>
                </p>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    color: 'var(--g33-or)',
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.18em',
                    fontWeight: 500,
                    position: 'relative',
                  }}
                >
                  Open day {day}
                  <ArrowRight size={14} />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OPERATIONAL TOOLKIT — inline, no download buttons */}
      {toolkit && toolkit.length > 0 && (
        <section style={{ background: 'var(--g33-cream-warm)', padding: '120px 32px' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>
            <Reveal>
              <div className="g33-eyebrow" style={{ marginBottom: 16 }}>
                Included in the program
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2
                className="g33-display"
                style={{
                  fontSize: 'clamp(40px, 5vw, 64px)',
                  marginBottom: 16,
                  maxWidth: 800,
                }}
              >
                Your operational toolkit.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p
                style={{
                  color: 'var(--g33-charcoal)',
                  fontSize: 17,
                  lineHeight: 1.7,
                  maxWidth: 720,
                  marginBottom: 64,
                }}
              >
                The operational backbone of the program — field references intended
                to outlive the cohort itself.
              </p>
            </Reveal>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 32,
              }}
              data-testid="toolkit-grid"
            >
              {toolkit.map((t, i) => (
                <Reveal key={t.slug} delay={i * 0.05}>
                  <article className="g33-feature-card" data-testid={`toolkit-${t.slug}`}>
                    <div className="g33-feature-card-icon" aria-hidden="true">
                      <FileText size={22} strokeWidth={1.4} />
                    </div>
                    <h3 className="g33-display g33-feature-card-title">
                      {t.title}
                    </h3>
                    <p className="g33-feature-card-body">
                      {t.tagline}
                    </p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section style={{ padding: '140px 32px', textAlign: 'center' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <Reveal>
            <div className="g33-eyebrow" style={{ marginBottom: 24 }}>Ready</div>
            <h2
              className="g33-display"
              style={{ fontSize: 'clamp(36px, 4.5vw, 56px)', marginBottom: 40 }}
            >
              The complete program is open.
            </h2>
            <Link to="/gate33-program/day/1" className="g33-btn-primary">
              Begin Day 1 <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
};

// =====================================================================
// DAY OVERVIEW
// =====================================================================
export const DayOverview = () => {
  const { day } = useParams();
  const dayN = parseInt(day, 10);
  const { modules } = useProgram();
  const items = modules.filter((m) => m.day === dayN).sort((a, b) => a.order - b.order);
  const titles = DAY_TITLES;

  return (
    <>
      <section style={{ padding: '160px 32px 80px', maxWidth: 1280, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 48,
            marginBottom: 24,
            flexWrap: 'wrap',
          }}
        >
          <div className="g33-mega-numeral">{String(dayN).padStart(2, '0')}</div>
          <div style={{ flex: 1, minWidth: 280, paddingBottom: 24 }}>
            <div className="g33-eyebrow" style={{ marginBottom: 16 }}>Day {dayN}</div>
            <h1
              className="g33-display"
              style={{ fontSize: 'clamp(36px, 5vw, 64px)', marginBottom: 16 }}
            >
              {titles[dayN] || 'Day'}
            </h1>
            <p style={{ color: 'var(--g33-charcoal)', fontSize: 18, maxWidth: 640 }} data-testid={`day-${dayN}-meta`}>
              {items.length} modules · {formatHours(dayMinutes(modules, dayN))}
            </p>
          </div>
        </div>
        <GoldDivider />
      </section>

      <DayTimetable day={dayN} />

      {/* Day 2 — narrative arc infographic */}
      {dayN === 2 && (
        <section style={{ padding: '0 32px 80px', maxWidth: 1280, margin: '0 auto' }} data-testid="day2-narrative-arc">
          <div className="g33-eyebrow" style={{ marginBottom: 32, color: 'var(--g33-or-deep)' }}>
            The narrative arc — order to recovery
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 0,
              position: 'relative',
              alignItems: 'start',
              padding: '24px 0 0',
            }}
          >
            {/* Connecting line */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 56,
                left: 'calc(12.5% + 24px)',
                right: 'calc(12.5% + 24px)',
                height: 1,
                background: 'linear-gradient(to right, rgba(201, 169, 97, 0.15), rgba(201, 169, 97, 0.55), rgba(201, 169, 97, 0.55), rgba(201, 169, 97, 0.15))',
              }}
            />
            {[
              { num: '07', label: 'Order' },
              { num: '08', label: 'Plate' },
              { num: '09', label: 'Serve' },
              { num: '10', label: 'Recover' },
            ].map((step) => ({ ...step, sub: items.find((x) => x.module_number === Number(step.num))?.title })).map((step, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, position: 'relative', zIndex: 1 }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'var(--g33-creme)',
                    border: '1px solid var(--g33-or)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'var(--g33-font-display)',
                    fontSize: 22,
                    fontWeight: 300,
                    color: 'var(--g33-or-deep)',
                    letterSpacing: '0.02em',
                  }}
                >
                  {step.num}
                </div>
                <div
                  className="g33-display"
                  style={{
                    fontSize: 'clamp(20px, 2vw, 26px)',
                    color: 'var(--g33-noir)',
                    fontWeight: 400,
                    letterSpacing: '0.01em',
                  }}
                >
                  {step.label}
                </div>
                <div
                  className="g33-eyebrow"
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.14em',
                    color: 'var(--g33-charcoal)',
                    textAlign: 'center',
                    maxWidth: 160,
                    opacity: 0.7,
                  }}
                >
                  {step.sub}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section style={{ padding: '0 32px 160px', maxWidth: 1280, margin: '0 auto' }}>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.map((m, i) => (
            <Reveal key={m.slug} delay={i * 0.06}>
              <li>
                <Link
                  to={`/gate33-program/module/${m.slug}`}
                  data-testid={`day-${dayN}-module-${m.module_number}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '120px 1fr auto',
                    gap: 32,
                    padding: '40px 0',
                    borderBottom: '1px solid var(--g33-ligne)',
                    textDecoration: 'none',
                    color: 'inherit',
                    alignItems: 'baseline',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239, 232, 218, 0.4)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div
                    style={{
                      fontFamily: 'var(--g33-font-display)',
                      fontWeight: 300,
                      fontSize: 'clamp(48px, 5vw, 72px)',
                      color: 'var(--g33-or)',
                      lineHeight: 1,
                    }}
                  >
                    {String(m.module_number).padStart(2, '0')}
                  </div>
                  <div>
                    <h2
                      className="g33-display"
                      style={{ fontSize: 28, marginBottom: 8, lineHeight: 1.2 }}
                    >
                      {m.title}
                    </h2>
                    <p
                      style={{
                        fontFamily: 'var(--g33-font-display)',
                        fontStyle: 'italic',
                        color: 'var(--g33-charcoal)',
                        fontSize: 16,
                        lineHeight: 1.5,
                      }}
                    >
                      {m.hook}
                    </p>
                  </div>
                  <div className="g33-eyebrow" style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {m.duration_min} min
                  </div>
                </Link>
              </li>
            </Reveal>
          ))}
        </ul>

        <div
          style={{
            marginTop: 80,
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 24,
          }}
        >
          <Link to="/gate33-program" className="g33-btn-ghost">← Back to program</Link>
          {dayN < 3 && (
            <Link to={`/gate33-program/day/${dayN + 1}`} className="g33-btn-primary">
              Day {dayN + 1} <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </section>
    </>
  );
};
